import { useEffect, useState } from 'react';
import type { ServerDoc, ServerLiveStats, NodeCommand } from '@panel/shared';
import { dispatchAction, useStore } from '../lib/store.js';
import { api } from '../lib/api.js';
import {
  IconBackup,
  IconBpWipe,
  IconClose,
  IconForceStop,
  IconPlay,
  IconRestart,
  IconStop,
  IconUpdate,
  IconWipe,
} from './Icons.js';

interface Props {
  server: ServerDoc;
  live: ServerLiveStats | undefined;
  nodeOnline: boolean;
}

const AUTO_DISMISS_MS = 30_000;

export default function ActionPanel({ server, live, nodeOnline }: Props) {
  const [confirm, setConfirm] = useState<null | { kind: NodeCommand; label: string; args?: Record<string, unknown> }>(null);
  const [busy, setBusy] = useState(false);
  const [latestActionId, setLatestActionId] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const action = useStore((s) => (latestActionId ? s.actions.get(latestActionId) : null));

  // Auto-dismiss the result panel after AUTO_DISMISS_MS once it lands in 'ok'.
  // Errors stay visible until the user dismisses or starts another action so
  // failure context isn't lost.
  useEffect(() => {
    if (!action || action.status !== 'ok' || dismissed) return;
    const t = setTimeout(() => setDismissed(true), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [action?.status, latestActionId, dismissed]);

  const isOnline = live?.status === 'online' || live?.status === 'restarting';
  const isOffline = live?.status === 'offline' || !live;
  const disabledAll = !nodeOnline;

  const send = (cmd: NodeCommand, args?: Record<string, unknown>) => {
    setBusy(true);
    setDismissed(false);
    const id = dispatchAction(server.Server_ID, cmd, args);
    setLatestActionId(id);
    setTimeout(() => setBusy(false), 2000);
    setConfirm(null);
  };

  const requestBackup = async () => {
    setBusy(true);
    try {
      await api.post('/api/backups/trigger', { Server_ID: server.Server_ID });
    } catch (err) {
      alert(`Backup failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <header className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-fg">Actions</h3>
        {!nodeOnline && (
          <span className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-status-error/10 text-status-error ring-1 ring-status-error/30">
            Node offline
          </span>
        )}
      </header>

      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <button
            className="btn-primary justify-start"
            onClick={() => send('start')}
            disabled={disabledAll || busy || isOnline}
          >
            <IconPlay /> Start
          </button>
          <button
            className="btn-ghost justify-start"
            onClick={() => setConfirm({ kind: 'soft_stop', label: 'Soft Stop' })}
            disabled={disabledAll || busy || !isOnline}
          >
            <IconStop /> Soft Stop
          </button>
          <button
            className="btn-ghost justify-start"
            onClick={() => setConfirm({ kind: 'restart', label: 'Restart' })}
            disabled={disabledAll || busy}
          >
            <IconRestart /> Restart
          </button>
          <button
            className="btn-danger justify-start"
            onClick={() => setConfirm({ kind: 'force_stop', label: 'Force Stop' })}
            disabled={disabledAll || busy || isOffline}
          >
            <IconForceStop /> Force Stop
          </button>
        </div>

        <div className="divider !my-3" />

        <button
          className="btn-ghost justify-start w-full"
          onClick={() => setConfirm({ kind: 'update', label: 'Update (Steam + Oxide)' })}
          disabled={disabledAll || busy || isOnline}
          title={isOnline ? 'Stop server first' : ''}
        >
          <IconUpdate /> Update (Steam + Oxide)
        </button>
        <div className="grid grid-cols-2 gap-2">
          <button
            className="btn-ghost justify-start"
            onClick={() => setConfirm({ kind: 'wipe', label: 'Map Wipe', args: { blueprints: false, extras: server.Wipe_Extras } })}
            disabled={disabledAll || busy || server.maps.length === 0}
            title={server.maps.length === 0 ? 'No maps in queue' : ''}
          >
            <IconWipe /> Map Wipe
          </button>
          <button
            className="btn-ghost justify-start"
            onClick={() => setConfirm({ kind: 'wipe', label: 'Map + BP Wipe', args: { blueprints: true, extras: server.Wipe_Extras } })}
            disabled={disabledAll || busy || server.maps.length === 0}
          >
            <IconBpWipe /> BP Wipe
          </button>
        </div>
        <button className="btn-ghost justify-start w-full" onClick={requestBackup} disabled={disabledAll || busy}>
          <IconBackup /> Backup Now
        </button>
      </div>

      {action && !dismissed && (
        <div className="mt-4 pt-4 border-t border-bg-border">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-fg-muted truncate">{action.message ?? 'Working…'}</span>
            <div className="flex items-center gap-2 shrink-0">
              <span className="tabular-nums text-fg-dim font-medium">{action.progress}%</span>
              {(action.status === 'ok' || action.status === 'error') && (
                <button
                  type="button"
                  onClick={() => setDismissed(true)}
                  className="btn-icon !w-5 !h-5"
                  aria-label="Dismiss"
                  title="Dismiss"
                >
                  <IconClose className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
          <div className="h-1 bg-bg-border rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${
                action.status === 'error' ? 'bg-status-error' :
                action.status === 'ok' ? 'bg-status-online' : 'bg-status-updating'
              }`}
              style={{ width: `${action.progress}%` }}
            />
          </div>
          {action.status === 'error' && (
            <div className="mt-2 text-xs text-status-error">{action.error}</div>
          )}
          {latestActionId && stepsForAction(latestActionId).length > 0 && (
            <Stepper
              steps={stepsForAction(latestActionId)}
              progress={action.progress}
              status={action.status}
            />
          )}
          {action.consoleLines.length > 0 && (
            <pre className="mt-3 max-h-48 overflow-y-auto bg-bg p-2.5 rounded-md text-[11px] font-mono text-fg-muted whitespace-pre-wrap border border-bg-border">
              {action.consoleLines.join('\n')}
            </pre>
          )}
        </div>
      )}

      {confirm && (
        <Modal onCancel={() => setConfirm(null)} onConfirm={() => send(confirm.kind, confirm.args)} label={confirm.label} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stepper for multi-stage actions (currently just `update`).
// Steps are derived from the action.progress percentage emitted by the agent.
// Keep these endPct values aligned with the progress(...) calls in
// node-agent/src/handlers/update.ts — if those shift, update here too.
// ---------------------------------------------------------------------------

interface Step { label: string; endPct: number }

function actionCommand(actionId: string): string | null {
  const dash = actionId.indexOf('-');
  return dash > 0 ? actionId.slice(0, dash) : null;
}

function stepsForAction(actionId: string): Step[] {
  switch (actionCommand(actionId)) {
    case 'update':
      return [
        { label: 'Manifest', endPct: 10 },
        { label: 'SteamCMD', endPct: 70 },
        { label: 'Oxide', endPct: 94 },
        { label: 'Finalize', endPct: 100 },
      ];
    default:
      return [];
  }
}

function Stepper({ steps, progress, status }: { steps: Step[]; progress: number; status: 'pending' | 'ok' | 'error' }) {
  return (
    <div className="mt-4 flex items-start">
      {steps.map((step, idx) => {
        const prevEnd = idx === 0 ? 0 : steps[idx - 1].endPct;
        let state: 'done' | 'active' | 'pending' | 'failed';
        if (status === 'error' && progress >= prevEnd && progress < step.endPct) state = 'failed';
        else if (status === 'ok' || progress >= step.endPct) state = 'done';
        else if (progress >= prevEnd) state = 'active';
        else state = 'pending';

        const isLast = idx === steps.length - 1;
        return (
          <div key={step.label} className="flex-1 flex flex-col items-center relative">
            <div className="flex items-center w-full">
              <div className={`flex-1 h-px ${idx === 0 ? 'invisible' : state === 'done' ? 'bg-status-online' : state === 'failed' ? 'bg-status-error' : 'bg-bg-border'}`} />
              <StepDot state={state} />
              <div className={`flex-1 h-px ${isLast ? 'invisible' : (state === 'done' ? 'bg-status-online' : 'bg-bg-border')}`} />
            </div>
            <div className={`text-[10px] mt-1.5 ${
              state === 'done' ? 'text-fg-muted'
                : state === 'active' ? 'text-fg'
                : state === 'failed' ? 'text-status-error'
                : 'text-fg-dim'
            }`}>
              {step.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StepDot({ state }: { state: 'done' | 'active' | 'pending' | 'failed' }) {
  const base = 'w-5 h-5 rounded-full border flex items-center justify-center shrink-0 transition-colors';
  if (state === 'done') {
    return (
      <div className={`${base} bg-status-online border-status-online`}>
        <svg width={10} height={10} viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M2.5 6.5l2.5 2.5 4.5-5" />
        </svg>
      </div>
    );
  }
  if (state === 'active') {
    return (
      <div className={`${base} bg-bg border-status-updating`}>
        <span className="w-1.5 h-1.5 rounded-full bg-status-updating animate-pulse" />
      </div>
    );
  }
  if (state === 'failed') {
    return (
      <div className={`${base} bg-status-error/10 border-status-error`}>
        <svg width={10} height={10} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" className="text-status-error" aria-hidden>
          <path d="M3 3l6 6M9 3l-6 6" />
        </svg>
      </div>
    );
  }
  return <div className={`${base} bg-bg-raised border-bg-border`} />;
}

function Modal({ label, onCancel, onConfirm }: { label: string; onCancel: () => void; onConfirm: () => void }) {
  return (
    <div className="fixed inset-0 z-50 bg-bg/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onCancel}>
      <div
        className="card p-6 w-full max-w-md shadow-modal animate-slide-up"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
      >
        <h3 id="confirm-title" className="font-semibold text-lg mb-2">Confirm: {label}</h3>
        <p className="text-sm text-fg-muted mb-5">
          Are you sure you want to <span className="text-fg font-medium">{label.toLowerCase()}</span> this server?
        </p>
        <div className="flex justify-end gap-2">
          <button className="btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn-primary" onClick={onConfirm} autoFocus>Confirm</button>
        </div>
      </div>
    </div>
  );
}
